import { Box, makeStyles } from "@material-ui/core";
import * as React from "react";

import { fontFamilies, layoutTokens } from "@/designTokens";
import theme from "@/theme";
import { isAllowedUrl } from "@/utilities/navigation";

import { LearningArticleNode } from "../data/articleTypes";

const colors = theme.palette.semantic;
const useStyles = makeStyles(() => ({
  body: {
    minWidth: 0,
    fontSize: 14,
    lineHeight: 1.5,
    color: colors["text/primary"],
    display: "flex",
    flexDirection: "column",
    gap: theme.spacing(3),
    "& p": { margin: 0 },
    "& strong, & b": { fontWeight: 700 },
    "& ul, & ol": { margin: 0, paddingLeft: theme.spacing(2) },
    "& li + li": { marginTop: theme.spacing(1) },
    "& h2, & h3, & h4, & h5, & h6": { margin: 0, fontSize: 16, lineHeight: 1.3, fontWeight: 700 },
    "& a": { color: colors["action/primary"], overflowWrap: "anywhere" },
    "& hr": { border: 0, borderTop: `1px solid ${colors["border/default"]}`, width: "100%", margin: 0 },
    "& aside": {
      padding: theme.spacing(2),
      borderRadius: layoutTokens.radius.md,
      backgroundColor: colors["surface/base"],
      display: "flex",
      flexDirection: "column",
      gap: theme.spacing(1),
    },
    "& aside > :first-child": { fontWeight: 700 },
    "& [data-role='product']": { backgroundColor: colors["status/info-bg"] },
    "& [data-role='product'] > :first-child": { color: colors["action/primary"], fontSize: 12 },
    "& [data-role='formula']": {
      padding: theme.spacing(2),
      textAlign: "center",
      borderRadius: layoutTokens.radius.md,
      backgroundColor: colors["surface/base"],
      overflowWrap: "anywhere",
    },
    "& [data-heading]": { display: "flex", alignItems: "flex-start", gap: theme.spacing(1) },
    "& [data-number]": {
      width: 26,
      height: 26,
      flexShrink: 0,
      borderRadius: layoutTokens.radius.full,
      backgroundColor: colors["action/primary"],
      color: colors["text/inverse"],
      display: "inline-flex",
      alignItems: "center",
      justifyContent: "center",
      fontFamily: fontFamilies.number,
      fontSize: 14,
      fontWeight: 600,
    },
    "& [data-heading-text]": { paddingTop: 2 },
    "& [data-role='comparison']": { display: "grid", gridTemplateColumns: "minmax(0, 1fr)", gap: theme.spacing(3) },
    "& [data-role='comparison'] > section": {
      minWidth: 0,
      display: "flex",
      flexDirection: "column",
      gap: theme.spacing(1.5),
    },
    "& [data-role='comparison'] [data-heading-text]": { overflowWrap: "anywhere" },
    "& [data-role='comparison'] ul": {
      paddingLeft: theme.spacing(2),
      borderRadius: layoutTokens.radius.md,
      backgroundColor: colors["surface/base"],
    },
    "& [data-role='comparison'] [data-role='advantages'] li::marker": { color: colors["status/success"] },
    "& [data-role='comparison'] [data-role='risks'] li::marker": { color: colors["status/error"] },
    "& [data-table]": { overflowX: "auto", maxWidth: "100%" },
    "& table": { borderCollapse: "collapse", width: "100%" },
    "& td, & th": { border: `1px solid ${colors["border/default"]}`, padding: theme.spacing(1), textAlign: "left" },
    "& blockquote": {
      margin: 0,
      padding: theme.spacing(2),
      borderLeft: `3px solid ${colors["border/strong"]}`,
      backgroundColor: colors["surface/base"],
    },
    [theme.breakpoints.up("md")]: {
      padding: theme.spacing(5),
      borderRadius: layoutTokens.radius.lg,
      backgroundColor: colors["surface/card"],
      fontSize: 16,
      gap: theme.spacing(4),
      "& h2, & h3, & h4, & h5, & h6": { fontSize: 20 },
    },
    [theme.breakpoints.up("lg")]: {
      "& [data-role='comparison']": { gridTemplateColumns: "repeat(3,minmax(0,1fr))", gap: theme.spacing(3) },
      "& [data-role='comparison'] > section": { display: "grid", gridTemplateRows: "subgrid", gridRow: "span 4" },
      "& [data-role='comparison'] h2": { fontSize: 16 },
    },
  },
}));
const allowedTags = new Set(
  "p h2 h3 h4 h5 h6 ul ol li strong em b i u br a table tr td th thead tbody tfoot caption blockquote hr code sub sup aside section div".split(
    " "
  )
);
export const articleNodeText = (node: LearningArticleNode): string =>
  node.text ?? (node.children ?? []).map(articleNodeText).join("");
export const LearningArticleBody: React.FC<{ nodes: LearningArticleNode[]; onNavigate: (href: string) => void }> = ({
  nodes,
  onNavigate,
}) => {
  const classes = useStyles();
  const render = (node: LearningArticleNode, key: string): React.ReactNode => {
    if (node.text !== undefined) return node.text;
    if (!node.tag || !allowedTags.has(node.tag)) return null;
    const children = node.children?.map((child, index) => render(child, `${key}-${index}`));
    if (/^h[2-6]$/.test(node.tag)) {
      const text = articleNodeText(node);
      const numbered = text.match(/^\s*(\d+)[.．、]\s*([\s\S]*)$/);
      return (
        <h2 key={key} data-heading>
          {numbered ? (
            <>
              <span data-number>{numbered[1]}</span>
              <span data-heading-text>{numbered[2]}</span>
            </>
          ) : (
            children
          )}
        </h2>
      );
    }
    if (node.tag === "a") {
      if (!node.href?.startsWith("https://")) return <React.Fragment key={key}>{children}</React.Fragment>;
      const href = node.href;
      return (
        <a
          key={key}
          href={href}
          target={isAllowedUrl(href) ? undefined : "_blank"}
          rel="noopener noreferrer"
          onClick={(event) => {
            if (isAllowedUrl(href)) {
              event.preventDefault();
              onNavigate(href);
            }
          }}
        >
          {children}
        </a>
      );
    }
    const props = {
      key,
      "data-role": node.role,
      ...(node.colspan ? { colSpan: node.colspan } : {}),
      ...(node.rowspan ? { rowSpan: node.rowspan } : {}),
    };
    if (node.tag === "br" || node.tag === "hr") return React.createElement(node.tag, props);
    if (node.tag === "table")
      return (
        <div key={key} data-table>
          {React.createElement(node.tag, props, children)}
        </div>
      );
    return React.createElement(node.tag, props, children);
  };
  return (
    <Box className={classes.body} data-learning-body>
      {nodes.map((node, index) => render(node, String(index)))}
    </Box>
  );
};
